import type {
  FollowAckResponse,
  FollowForceEndPayload,
  FollowNudgeReceivedPayload,
  LeaderMirrorPayload,
} from "@cold-case/shared/player-follow";
import type { GameZoneId } from "@cold-case/shared/player-pose";
import type { Socket } from "socket.io-client";

import { isFollowConfirmOpen, openFollowConfirm } from "../lib/follow-confirm.js";
import {
  setPlayerOverlayOpen,
} from "../lib/player-input-gate.js";

import type { GameScene } from "./GameScene.js";
import { followMoveSpeedPxS, followSlotWorldPosition } from "./player-follow-math.js";
import type { PlayableDir } from "./playable-facing.js";
import { RUN_PLAYER_SPEED_PX_S } from "./design-resolution.js";

export type PlayerFollowDeps = {
  getSessionId: () => string;
  getSocket: () => Socket | null;
  getSelfUserId: () => string;
  getPlayerCount: () => number;
  getGameScene: () => GameScene | undefined;
  labelForUserId: (userId: string) => string;
  dismissLocalOverlays: () => void;
  matchLeaderWorld: (opts: {
    zone: GameZoneId;
    buildingId: string | null;
    buildingDisplayName?: string;
  }) => Promise<void>;
  applyLeaderMirror: (mirror: LeaderMirrorPayload) => void;
  onFollowingChange: (following: boolean) => void;
  showStatus: (text: string, ms?: number) => void;
  onLeaderWorldChange: (zone: GameZoneId, buildingId: string | null) => void;
};

let deps: PlayerFollowDeps | null = null;
let leaderUserId: string | null = null;
let followSlot = 0;
let socketUnbind: (() => void) | null = null;

export function initPlayerFollow(d: PlayerFollowDeps): void {
  deps = d;
  bindFollowSockets(d.getSocket());
}

export function rebindPlayerFollowSocket(sock: Socket | null): void {
  bindFollowSockets(sock);
}

function bindFollowSockets(sock: Socket | null): void {
  socketUnbind?.();
  socketUnbind = null;
  if (!sock) return;

  const onAck = (payload: FollowAckResponse): void => {
    if (!payload.ok) {
      const msg =
        payload.error === "leader_full"
          ? "That detective already has two partners following."
          : payload.error === "circular_follow"
            ? "You cannot follow someone who is following you."
            : payload.error === "self_follow"
              ? "You cannot follow yourself."
              : payload.error === "forbidden"
                ? "Following is not available right now."
                : payload.error === "leader_missing"
                  ? "That detective is no longer in this session."
                  : "Could not follow — try again.";
      deps?.showStatus(msg, 5000);
      return;
    }
    void enterFollowMode(payload);
  };

  const onForceEnd = (payload: FollowForceEndPayload): void => {
    if (payload.sessionId !== deps?.getSessionId()) return;
    if (payload.reason === "accusation") {
      endFollowMode(false);
      return;
    }
    const targetLeader = payload.leaderUserId;
    if (targetLeader && leaderUserId === targetLeader) {
      endFollowMode(false);
    }
  };

  const onMirror = (payload: { leaderUserId: string; mirror: LeaderMirrorPayload }) => {
    if (!isFollowingLeader() || payload.leaderUserId !== leaderUserId) return;
    deps?.applyLeaderMirror(payload.mirror);
  };

  const onNudge = (payload: FollowNudgeReceivedPayload): void => {
    if (payload.sessionId !== deps?.getSessionId()) return;
    if (payload.leaderUserId !== deps?.getSelfUserId()) return;
    deps?.getGameScene()?.game.events.emit("ccc_follow_nudge_received");
  };

  sock.on("game:follow_ack", onAck);
  sock.on("game:follow_force_end", onForceEnd);
  sock.on("game:leader_mirror", onMirror);
  sock.on("game:follow_nudge", onNudge);

  const onZone = (payload: {
    userId: string;
    zone: string;
    buildingId?: string | null;
  }) => {
    if (!isFollowingLeader() || payload.userId !== leaderUserId) return;
    const zone =
      payload.zone === "maple_heights" ? "maple_heights" : "downtown";
    const buildingId =
      typeof payload.buildingId === "string" && payload.buildingId.length > 0
        ? payload.buildingId
        : null;
    deps?.onLeaderWorldChange(zone, buildingId);
  };

  sock.on("game:zone_change", onZone);

  socketUnbind = () => {
    sock.off("game:follow_ack", onAck);
    sock.off("game:follow_force_end", onForceEnd);
    sock.off("game:leader_mirror", onMirror);
    sock.off("game:follow_nudge", onNudge);
    sock.off("game:zone_change", onZone);
  };
}

export function emitFollowNudge(): void {
  if (!isFollowingLeader() || !deps) return;
  const sock = deps.getSocket();
  const sessionId = deps.getSessionId();
  const leader = getFollowLeaderUserId();
  if (!sock?.connected || !sessionId || !leader) return;
  sock.emit("game:follow_nudge", { sessionId, leaderUserId: leader });
}

export function isFollowingLeader(): boolean {
  return leaderUserId !== null;
}

export function getFollowLeaderUserId(): string | null {
  return leaderUserId;
}

export function getFollowSlot(): number {
  return followSlot;
}

export function canInitiateFollow(): boolean {
  if (!deps) return false;
  if (deps.getPlayerCount() <= 1) return false;
  if (isFollowingLeader()) return false;
  return true;
}

export function onPeerTapped(userId: string): void {
  if (!deps) {
    return;
  }
  if (!canInitiateFollow()) {
    if (isFollowingLeader()) {
      deps.showStatus("Stop following before tapping another detective.", 5000);
    } else if (deps.getPlayerCount() <= 1) {
      deps.showStatus("Need at least two detectives in this case to follow a partner.", 5000);
    }
    return;
  }
  if (userId === deps.getSelfUserId()) return;
  if (isFollowConfirmOpen()) {
    deps.showStatus("Finish the follow prompt first.", 5000);
    return;
  }

  const label = deps.labelForUserId(userId);
  openFollowConfirm({
    message: `Follow ${label}?`,
    onConfirm: () => {
      const sock = deps?.getSocket();
      const sessionId = deps?.getSessionId();
      if (!sock?.connected || !sessionId) {
        deps?.showStatus("Not connected — try again.", 5000);
        return;
      }
      sock.emit("game:follow_start", { sessionId, leaderUserId: userId });
    },
  });
}

async function enterFollowMode(
  ack: Extract<FollowAckResponse, { ok: true }>
): Promise<void> {
  if (!deps) return;
  deps.dismissLocalOverlays();
  leaderUserId = ack.leaderUserId;
  followSlot = ack.followSlot;

  await deps.matchLeaderWorld({
    zone: ack.leaderZone,
    buildingId: ack.leaderBuildingId,
  });

  const gs = deps.getGameScene();
  if (!gs) {
    endFollowMode(true);
    return;
  }

  const cameraOk = gs.enterFollowMode(
    ack.leaderUserId,
    ack.leaderFacing as PlayableDir,
    followSlot
  );
  if (!cameraOk) {
    deps.showStatus("Could not follow — partner is not nearby.", 5000);
    endFollowMode(true);
    return;
  }

  setPlayerOverlayOpen("follow-leader", true);
  deps.onFollowingChange(true);
}

export function requestStopFollowing(): void {
  endFollowMode(true);
}

export function endFollowMode(emitStop: boolean): void {
  if (!leaderUserId) return;
  const wasLeader = leaderUserId;
  leaderUserId = null;
  followSlot = 0;
  setPlayerOverlayOpen("follow-leader", false);

  const gs = deps?.getGameScene();
  gs?.exitFollowMode();

  if (emitStop) {
    const sock = deps?.getSocket();
    const sessionId = deps?.getSessionId();
    if (sock?.connected && sessionId) {
      sock.emit("game:follow_stop", { sessionId });
    }
  }

  deps?.onFollowingChange(false);
  void wasLeader;
}

/** Called each frame from GameScene while following. */
export function tickFollowMovement(
  gs: GameScene,
  delta: number
): void {
  if (!leaderUserId) return;
  const leader = gs.getPeerRig(leaderUserId);
  if (!leader) {
    endFollowMode(true);
    return;
  }

  const facing = gs.getPeerFacing(leaderUserId) ?? "south";
  const slot = followSlotWorldPosition(leader.x, leader.y, facing, followSlot);
  const body = gs.getPlayerBody();
  if (!body) return;

  const dx = slot.x - gs.getPlayerRigX();
  const dy = slot.y - gs.getPlayerRigY();
  const dist = Math.hypot(dx, dy);
  if (dist < 6) {
    body.setVelocity(0, 0);
    return;
  }

  const speed = followMoveSpeedPxS(dist, RUN_PLAYER_SPEED_PX_S);
  body.setVelocity((dx / dist) * speed, (dy / dist) * speed);
  gs.syncPlayerFacingFromVelocity(body.velocity.x, body.velocity.y);
}

export function disposePlayerFollow(): void {
  endFollowMode(false);
  socketUnbind?.();
  socketUnbind = null;
  deps = null;
}
